/**
 * EVAnalysisPanel — shared Enterprise Value visualization for the focal
 * company. Two render modes:
 *
 *   `summary` → just the Revenue→EV Sankey (one big visual). Used inline in
 *   the wizard (Step 5 Review) to give the user a "story" of how the focal
 *   company's revenue translates to enterprise value before they submit.
 *
 *   `full`    → the full "ev" section of the existing
 *   <FinancialDashboardTab />: EV decomposition, EBITDA analysis, EV
 *   composition, valuation metrics, key insights. Used inside CaseDetail's
 *   EV tab so the same depth as the standalone Company Research workspace is
 *   available without leaving the case.
 *
 * Both modes share the same TanStack Query cache key as Company Research
 * (`financial.company.<name>.<years>.<country>`) — no duplicate fetches.
 */
import { AlertCircle } from 'lucide-react';
import FinancialDashboardTab from '../financial/FinancialDashboardTab';
import ValuationSankeyChart from '../financial/charts/ValuationSankeyChart';

interface EVAnalysisPanelProps {
  /** Company name as stored in MongoDB. Required for the EV lookup. */
  companyName: string | null | undefined;
  /** Optional country hint ('US' | 'INDIA' | 'UK'). */
  country?: string;
  /**
   * `summary` → Revenue→EV Sankey only.
   * `full`    → Full EV dashboard section.
   * Default `full` matches CaseDetail's EV tab use.
   */
  mode?: 'summary' | 'full';
}

export default function EVAnalysisPanel({
  companyName,
  country: _country,
  mode = 'full',
}: EVAnalysisPanelProps) {
  if (!companyName) {
    return (
      <div className="flex items-center gap-2 p-4 bg-neutral-50 border border-neutral-200 rounded-lg text-neutral-600">
        <AlertCircle className="h-4 w-4 shrink-0 text-neutral-400" />
        <p className="text-sm">
          No company linked to this case yet. Link a Company Research record to see the EV story.
        </p>
      </div>
    );
  }

  if (mode === 'summary') {
    // Sankey is the single highest-signal EV visual — fits inline in a
    // wizard step without dwarfing the other review sections. The chart
    // pulls its own data via useSankeyData and handles its own loading
    // and error states, so no extra orchestration is needed here.
    return <ValuationSankeyChart companyName={companyName} />;
  }

  // Country prop intentionally not forwarded to FinancialDashboardTab in this
  // slice — CaseDetail doesn't yet resolve ui-config for the linked company
  // (Phase 1c is read-only for now). When CaseDetail wires ui-config in a
  // later slice, swap to `country={_country}`.
  return (
    <FinancialDashboardTab
      companyName={companyName}
      section="ev"
      country={_country}
    />
  );
}
